import type { PlotDevice } from './types.js';
import { PLOT_DEVICE_SCHEMA_VERSION } from './types.js';

/** Writerly mechanism categories for the seeded library. */
export const PLOT_DEVICE_CATEGORIES = [
  'tragedy',
  'surprise',
  'discovery',
  'conflict',
  'action',
  'reversal',
  'relationship',
  'mystery',
  'comedy',
  'routine',
] as const;

export type PlotDeviceCategory = (typeof PLOT_DEVICE_CATEGORIES)[number];

const CATEGORY_TEMPLATES: Record<PlotDeviceCategory, string[]> = {
  tragedy: [
    'Sudden loss',
    'Broken promise',
    'Quiet grief',
    'Regretted words',
    'Fading hope',
    'Unanswered call',
    'Empty chair',
    'Rain at closing',
    'Forgotten birthday',
    'Last shift',
  ],
  surprise: [
    'Unexpected visitor',
    'Found note',
    'Power flicker',
    'Wrong delivery',
    'Hidden talent',
    'Lucky find',
    'Alarm misfire',
    'Surprise audit',
    'Mystery gift',
    'Late announcement',
  ],
  discovery: [
    'Old photograph',
    'Hidden drawer',
    'Misfiled record',
    'Trail of crumbs',
    'Unmarked door',
    'Stale receipt',
    'Forgotten key',
    'Unread memo',
    'Cracked tile',
    'Bird nest',
  ],
  conflict: [
    'Schedule clash',
    'Borrowed item',
    'Broken rule',
    'Rival claim',
    'Misheard rumor',
    'Territory dispute',
    'Unfair task',
    'Cold silence',
    'Budget cut',
    'Policy change',
  ],
  action: [
    'Rush order',
    'Fire drill',
    'Chase scene',
    'Rescue attempt',
    'Improvised fix',
    'Midnight errand',
    'Sprint deadline',
    'Crowd surge',
    'Equipment swap',
    'Bold wager',
  ],
  reversal: [
    'Role swap',
    'Mistaken identity',
    'Plot twist',
    'Fortune flips',
    'Ally turns',
    'Plan B wins',
    'False alarm',
    'Second chance',
    'Upset outcome',
    'Quiet redemption',
  ],
  relationship: [
    'New alliance',
    'Awkward reunion',
    'Shared secret',
    'Trust test',
    'Kind gesture',
    'Jealous glance',
    'Mentor moment',
    'Rivalry softens',
    'Boundary talk',
    'Inside joke',
  ],
  mystery: [
    'Missing item',
    'Strange sound',
    'Anonymous tip',
    'Locked room',
    'Odd pattern',
    'Unmarked package',
    'Footprints',
    'Cipher note',
    'Vanishing act',
    'Clock anomaly',
  ],
  comedy: [
    'Slapstick spill',
    'Misplaced prop',
    'Auto-correct fail',
    'Talking over each other',
    'Wrong costume',
    'Pet interruption',
    'Echo chamber',
    'Overreaction',
    'Pun cascade',
    'Timing mishap',
  ],
  routine: [
    'Opening ritual',
    'Closing checklist',
    'Coffee run',
    'Weekly inventory',
    'Morning briefing',
    'Afternoon lull',
    'End-of-day tally',
    'Supply restock',
    'Shift handoff',
    'Quiet hour',
  ],
};

function buildSeededCatalog(): PlotDevice[] {
  const devices: PlotDevice[] = [];
  let index = 0;
  for (const category of PLOT_DEVICE_CATEGORIES) {
    const titles = CATEGORY_TEMPLATES[category];
    for (const title of titles) {
      index += 1;
      const id = `seed-${String(index).padStart(3, '0')}`;
      devices.push({
        deviceId: id,
        schemaVersion: PLOT_DEVICE_SCHEMA_VERSION,
        category,
        title,
        description: `${title} — a ${category} beat suitable for many locations.`,
        trigger: `When daily rhythm or social tension allows a ${category} moment.`,
        intensity: index % 3 === 0 ? 'intense' : index % 2 === 0 ? 'moderate' : 'mild',
        source: 'seeded',
        safetyStatus: 'reviewed',
      });
    }
  }
  return devices;
}

/** Canonical 100-device seeded library (deterministic, reviewable). */
export const SEEDED_PLOT_DEVICE_CATALOG: readonly PlotDevice[] = buildSeededCatalog();
